import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import { classNames } from "primereact/utils";
import FamilyApi from "../../services/FamilyApi";
import { useAuth } from "../../context/AuthContext";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const CreateFamilyForm: React.FC = () => {
  const { plantId } = useAuth();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const { displayToast } = useToast();
  const navigate = useNavigate();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessages } = useTranslation("Messages");

  const nameRef = useRef<any>(null);
  const descriptionRef = useRef<any>(null);

  const handleSubmit = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();

    if (!name.trim() || !description.trim()) {
      setError(true);
      displayToast(
        "error",
        translateMessages("error"),
        translateMessages("requiredFields"),
        false,
        3000,
      );
      if (!name.trim()) nameRef.current?.focus();
      else descriptionRef.current?.focus();
      return;
    }

    const payload = {
      name: name.trim(),
      description: description.trim(),
      plantId: plantId,
    };

    try {
      await FamilyApi.CreateFamily(payload);
      displayToast("success", translateForm("Success"), translateForm("familyCreated"), false, 3000);
      setName("");
      setDescription("");
      setError(false);
      navigate("/families");
    } catch (err: any) {
      const errorMessage = err?.data?.data || translateForm("familyNotCreated");
      displayToast("error", translateMessages("error"), errorMessage, false, 3000);
    }
  };

  const handleCancel = (): void => {
    setShowConfirmCancel(true);
  };

  return (
    <FormCard
      title={translateForm("createProductFamily")}
      icon="category"
      submitButtonLabel={translateForm("register")}
      cancelButtonLabel={translateForm("Cancel")}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      numColumns={12}
    >
      <div style={{ gridColumn: "span 12" }}>
        <Input
          ref={nameRef}
          type="text"
          label={translateForm("Name")}
          value={name}
          required
          onChange={(val: any) => {
            setName(val);
            setError(false);
          }}
          placeholder={`${translateForm("name")} *`}
          className={classNames("w-full", { "p-invalid border-red": error && !name.trim() })}
        />
        {error && !name.trim() && (
          <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>
            {translateForm("requiredField")}
          </small>
        )}
      </div>

      <div style={{ gridColumn: "span 12" }}>
        <Input
          ref={descriptionRef}
          type="textArea"
          label={translateForm("Description")}
          value={description}
          required
          rows={3}
          onChange={(val: any) => {
            setDescription(val);
            setError(false);
          }}
          placeholder={`${translateForm("description")} *`}
          className={classNames("w-full", { "p-invalid border-red": error && !description.trim() })}
        />
        {error && !description.trim() && (
          <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>
            {translateForm("requiredField")}
          </small>
        )}
      </div>

      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/families")}
      />
    </FormCard>
  );
};

export default CreateFamilyForm;
